import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Circuitry,
  Plus,
  Spinner,
  Power,
  Lightning,
  House,
  Trash,
  ArrowsClockwise,
  Warning,
  CurrencyCircleDollar,
  User,
  Buildings,
  Funnel,
  Gauge,
  ArrowRight,
  Pencil,
  MagnifyingGlass,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getErrorMessage } from "../lib/utils";
import { getDeviceFreshness, getFreshnessDisplay, timeAgoTr } from "../utils/deviceFreshness";
import Pagination from "../components/Pagination";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DevicesPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isAdmin = user?.role === "admin";
  const isStaff = user?.role === "staff";
  const hasPerm = (key) => !isStaff || user?.permissions?.[key] !== false;
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignUnitDialog, setAssignUnitDialog] = useState(false);
  const [editDeviceDialog, setEditDeviceDialog] = useState(false);
  const [editDeviceTarget, setEditDeviceTarget] = useState(null);
  const [editDeviceName, setEditDeviceName] = useState("");
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [controlLoading, setControlLoading] = useState({});
  const [bulkRefreshing, setBulkRefreshing] = useState(false);
  const [filter, setFilter] = useState("all");
  const [formData, setFormData] = useState({ tuya_device_id: "", name: "" });
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/customers`);
      setCustomers(res.data);
      if (res.data.length === 1) setSelectedCustomerId(res.data[0].id);
    } catch { /* ignore */ }
  }, []);

  const fetchDevices = useCallback(async () => {
    try {
      const [devRes, unitsRes] = await Promise.all([
        axios.get(`${API_URL}/api/devices`),
        user?.role !== "tenant" ? axios.get(`${API_URL}/api/all-units`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setDevices(devRes.data);
      setUnits(unitsRes.data || []);
    } catch (error) {
      toast.error("Cihazlar yuklenirken hata olustu");
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    fetchDevices();
    if (isAdmin) fetchCustomers();
  }, [fetchDevices, fetchCustomers, isAdmin]);

  const handleCreateDevice = async (e) => {
    e.preventDefault();
    if (isAdmin && !selectedCustomerId) {
      toast.error("Lutfen bir musteri secin");
      return;
    }
    setFormLoading(true);
    try {
      const url = isAdmin
        ? `${API_URL}/api/devices?customer_id=${selectedCustomerId}`
        : `${API_URL}/api/devices`;
      await axios.post(url, formData);
      toast.success("Sayac basariyla eklendi");
      setDialogOpen(false);
      setFormData({ tuya_device_id: "", name: "" });
      fetchDevices();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handleControlDevice = async (deviceId, action) => {
    setControlLoading((prev) => ({ ...prev, [deviceId]: true }));
    try {
      const response = await axios.post(`${API_URL}/api/devices/${deviceId}/control?action=${action}`);
      if (response.data.success) {
        toast.success(`Sayac ${action === "on" ? "acildi" : "kapatildi"}`);
        fetchDevices();
      } else {
        toast.error(response.data.error || "Islem basarisiz");
      }
    } catch (error) {
      toast.error("Cihaz kontrol hatasi");
    } finally {
      setControlLoading((prev) => ({ ...prev, [deviceId]: false }));
    }
  };

  const handleRefreshStatus = async (deviceId) => {
    setControlLoading((prev) => ({ ...prev, [`status_${deviceId}`]: true }));
    try {
      const response = await axios.get(`${API_URL}/api/devices/${deviceId}/status`);
      if (response.data.success) {
        toast.success("Sayac durumu guncellendi");
        fetchDevices();
      } else {
        toast.error(response.data.error || "Durum alinamadi");
      }
    } catch (error) {
      toast.error("Durum guncellenemedi");
    } finally {
      setControlLoading((prev) => ({ ...prev, [`status_${deviceId}`]: false }));
    }
  };

  const handleBulkRefresh = async () => {
    setBulkRefreshing(true);
    try {
      const res = await axios.post(`${API_URL}/api/devices/bulk-refresh`);
      toast.success(`${res.data.updated} sayac guncellendi${res.data.errors > 0 ? `, ${res.data.errors} hata` : ""}`);
      fetchDevices();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBulkRefreshing(false);
    }
  };

  const handleBalanceCheck = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/balance-check`);
      const d = res.data;
      if (d.pending_sync > 0) {
        toast.info(`${d.checked} cihaz kontrol edildi. ${d.zero_balance} sifir bakiye, ${d.pending_sync} cihaz senkron bekliyor.`);
      } else {
        toast.success(d.message);
      }
      fetchDevices();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    try {
      await axios.delete(`${API_URL}/api/devices/${deviceId}`);
      toast.success("Sayac silindi");
      fetchDevices();
    } catch (error) {
      toast.error("Silme islemi basarisiz");
    }
  };

  const openAssignUnitDialog = (device) => {
    setSelectedDevice(device);
    setAssignUnitDialog(true);
  };

  const handleAssignToUnit = async (unitId) => {
    if (!selectedDevice) return;
    setFormLoading(true);
    try {
      await axios.put(`${API_URL}/api/units/${unitId}/assign-device?device_id=${selectedDevice.id}`);
      toast.success("Sayac daireye atandi");
      setAssignUnitDialog(false);
      setSelectedDevice(null);
      fetchDevices();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handleUnassignDevice = async (device) => {
    if (!device.unit_id) return;
    if (!window.confirm(`${device.name} sayacini ${device.apartment_name} - ${device.unit_name} dairesinden cikarmak istediginize emin misiniz?`)) return;
    try {
      await axios.put(`${API_URL}/api/units/${device.unit_id}/remove-device`);
      toast.success("Sayac daireden cikarildi");
      fetchDevices();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const openEditDeviceDialog = (device) => {
    setEditDeviceTarget(device);
    setEditDeviceName(device.name);
    setEditDeviceDialog(true);
  };

  const handleUpdateDevice = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.put(`${API_URL}/api/devices/${editDeviceTarget.id}`, { name: editDeviceName });
      toast.success("Sayac guncellendi");
      setEditDeviceDialog(false);
      fetchDevices();
    } catch (error) { toast.error(getErrorMessage(error)); }
    finally { setFormLoading(false); }
  };

  const availableUnits = units.filter((u) => !u.device_id);

  // Filtering
  const filteredDevices = devices.filter((d) => {
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const nameMatch = d.name?.toLowerCase().includes(q);
      const aptMatch = d.apartment_name?.toLowerCase().includes(q);
      const unitMatch = d.unit_name?.toLowerCase().includes(q);
      const tenantMatch = d.tenant_name?.toLowerCase().includes(q);
      const customerMatch = d.customer_name?.toLowerCase().includes(q);
      const tuyaMatch = d.tuya_device_id?.toLowerCase().includes(q);
      if (!nameMatch && !aptMatch && !unitMatch && !tenantMatch && !customerMatch && !tuyaMatch) return false;
    }
    // Status filter
    if (filter === "online") return getDeviceFreshness(d) === "fresh_online";
    if (filter === "stale") return getDeviceFreshness(d) === "stale_online";
    if (filter === "offline") return getDeviceFreshness(d) === "offline";
    if (filter === "unknown") return getDeviceFreshness(d) === "unknown";
    if (filter === "unassigned") return !d.unit_id;
    if (filter === "low-balance") return d.balance_kwh !== null && d.balance_kwh !== undefined && d.balance_kwh <= 0;
    return true;
  });

  const unassignedCount = devices.filter((d) => !d.unit_id).length;
  const freshOnlineCount = devices.filter((d) => getDeviceFreshness(d) === "fresh_online").length;
  const staleOnlineCount = devices.filter((d) => getDeviceFreshness(d) === "stale_online").length;
  const offlineCount = devices.filter((d) => getDeviceFreshness(d) === "offline").length;
  const unknownCount = devices.filter((d) => getDeviceFreshness(d) === "unknown").length;
  const lowBalanceCount = devices.filter((d) => d.balance_kwh !== null && d.balance_kwh !== undefined && d.balance_kwh <= 0).length;

  // Paginate
  const paginatedDevices = filteredDevices.slice((page - 1) * pageSize, page * pageSize);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={32} className="animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="devices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">{t("devices.title")}</h1>
          <p className="text-slate-500 mt-1">{t("devices.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {user?.role !== "tenant" && (
            <>
              <Button
                variant="outline"
                onClick={handleBulkRefresh}
                disabled={bulkRefreshing}
                data-testid="bulk-refresh-btn"
              >
                <ArrowsClockwise size={16} className={`mr-2 ${bulkRefreshing ? "animate-spin" : ""}`} />
                {bulkRefreshing ? t("common.updating") : t("devices.bulk_update")}
              </Button>
              {hasPerm("device_control") && (
                <Button
                  variant="outline"
                  onClick={handleBalanceCheck}
                  data-testid="balance-check-btn"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Warning size={16} className="mr-2" />
                  {t("devices.kwh_check")}
                </Button>
              )}
              {hasPerm("device_add") && (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-device-btn">
                    <Plus size={18} className="mr-2" />
                    {t("devices.add")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>{t("devices.add_title")}</DialogTitle>
                    <DialogDescription>{t("devices.add_desc")}</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateDevice}>
                    <div className="space-y-4 py-4">
                      {isAdmin && (
                        <div className="space-y-2">
                          <Label>{t("common.customer")}</Label>
                          <Select value={selectedCustomerId} onValueChange={setSelectedCustomerId}>
                            <SelectTrigger data-testid="customer-select">
                              <SelectValue placeholder={t("common.select_customer")} />
                            </SelectTrigger>
                            <SelectContent>
                              {customers.map((c) => (
                                <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label htmlFor="tuya_device_id">{isAdmin ? t("admin.device_serial") : t("admin.device_serial")}</Label>
                        <Input
                          id="tuya_device_id"
                          placeholder={t("admin.device_serial")}
                          value={formData.tuya_device_id}
                          onChange={(e) => setFormData({ ...formData, tuya_device_id: e.target.value })}
                          required
                          data-testid="device-tuya-id-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="name">{t("devices.name")}</Label>
                        <Input
                          id="name"
                          placeholder={t("devices.name")}
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          data-testid="device-name-input"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
                      <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={formLoading} data-testid="device-submit-btn">
                        {formLoading ? t("common.creating") : t("common.add")}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              )}
            </>
          )}
        </div>
      </div>

      {/* Alerts */}
      {unassignedCount > 0 && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="unassigned-warning">
          <Warning size={24} className="text-amber-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">{unassignedCount} sayac daireye atanmamis</p>
            <p className="text-xs text-amber-600">Bu sayaclari kullanmak icin bir daireye atayin</p>
          </div>
        </div>
      )}

      {lowBalanceCount > 0 && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl" data-testid="low-balance-warning">
          <Warning size={24} className="text-red-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">{lowBalanceCount} {t("devices.low_kwh_warning")}</p>
            <p className="text-xs text-red-600">{t("devices.low_kwh_desc")}</p>
          </div>
        </div>
      )}

      {/* Stats + Search + Filter */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <Badge variant="outline" className="py-1.5 px-3">{t("devices.total")} {devices.length}</Badge>
          <Badge className="bg-emerald-100 text-emerald-700 py-1.5 px-3">Online {freshOnlineCount}</Badge>
          {staleOnlineCount > 0 && <Badge className="bg-amber-100 text-amber-700 py-1.5 px-3">Belirsiz {staleOnlineCount}</Badge>}
          <Badge className="bg-slate-100 text-slate-500 py-1.5 px-3">Offline {offlineCount}</Badge>
          {unknownCount > 0 && <Badge className="bg-blue-50 text-blue-500 py-1.5 px-3">Bilinmiyor {unknownCount}</Badge>}
          {unassignedCount > 0 && <Badge className="bg-amber-100 text-amber-700 py-1.5 px-3">{t("devices.unassigned")} {unassignedCount}</Badge>}
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder={t("devices.search_placeholder")}
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              className="pl-9 pr-8 h-9"
              data-testid="device-search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="device-clear-search-btn">
                <X size={14} />
              </button>
            )}
          </div>
          <Funnel size={16} className="text-slate-400" />
          <Select value={filter} onValueChange={(v) => { setFilter(v); setPage(1); }}>
            <SelectTrigger className="w-44" data-testid="filter-select">
              <SelectValue placeholder={t("common.filter")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("devices.filter_all")}</SelectItem>
              <SelectItem value="online">Online</SelectItem>
              <SelectItem value="stale">Belirsiz (Stale)</SelectItem>
              <SelectItem value="offline">Offline</SelectItem>
              <SelectItem value="unknown">Bilinmiyor</SelectItem>
              <SelectItem value="unassigned">{t("devices.unassigned")}</SelectItem>
              <SelectItem value="low-balance">{t("devices.low_balance")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Results info */}
      {(searchQuery || filter !== "all") && (
        <p className="text-sm text-slate-500" data-testid="device-filter-results">
          {filteredDevices.length} {t("common.results_found")}
          {searchQuery && <span> &middot; &quot;{searchQuery}&quot;</span>}
        </p>
      )}

      {/* Devices Table */}
      {filteredDevices.length === 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="py-16 text-center">
            <Circuitry size={64} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              {devices.length === 0 ? t("devices.no_devices") : t("devices.no_filter_match")}
            </h3>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold">{t("devices.meter")}</TableHead>
                    <TableHead className="text-xs font-semibold hidden sm:table-cell">{t("devices.location")}</TableHead>
                    <TableHead className="text-xs font-semibold hidden md:table-cell">{t("common.tenant")}</TableHead>
                    <TableHead className="text-xs font-semibold text-center">{t("common.status")}</TableHead>
                    <TableHead className="text-xs font-semibold text-right hidden sm:table-cell">{t("devices.power")}</TableHead>
                    <TableHead className="text-xs font-semibold text-right hidden sm:table-cell">{t("reports.consumption")}</TableHead>
                    <TableHead className="text-xs font-semibold text-right">kWh</TableHead>
                    {user?.role !== "tenant" && (
                      <TableHead className="text-xs font-semibold text-center">{t("common.actions")}</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedDevices.map((device) => {
                    const lowKwh = device.balance_kwh !== null && device.balance_kwh !== undefined && device.balance_kwh <= 0;
                    const unassigned = !device.unit_id;
                    const freshness = getDeviceFreshness(device);
                    const fDisplay = getFreshnessDisplay(freshness);
                    return (
                      <TableRow
                        key={device.id}
                        data-testid={`device-row-${device.id}`}
                        className={
                          lowKwh ? "bg-red-50/60 hover:bg-red-50" :
                          unassigned ? "bg-amber-50/60 hover:bg-amber-50" :
                          "hover:bg-slate-50"
                        }
                      >
                        {/* Sayac */}
                        <TableCell className="py-2.5">
                          <div className="flex items-center gap-2">
                            <Circuitry size={18} weight="duotone" className={fDisplay.iconClass} />
                            <div>
                              <p className="font-medium text-sm text-slate-900">{device.name}</p>
                              {isAdmin && <p className="text-[10px] text-slate-400 font-mono">{device.tuya_device_id}</p>}
                            </div>
                          </div>
                        </TableCell>
                        {/* Konum */}
                        <TableCell className="py-2.5 text-sm hidden sm:table-cell">
                          {device.apartment_name ? (
                            <span className="text-slate-700">{device.apartment_name} - {device.unit_name}</span>
                          ) : (
                            <span className="text-amber-600 text-xs font-medium">{t("devices.unassigned")}</span>
                          )}
                        </TableCell>
                        {/* Kiraci */}
                        <TableCell className="py-2.5 text-sm text-slate-700 hidden md:table-cell">
                          {device.tenant_name || <span className="text-slate-400">-</span>}
                        </TableCell>
                        {/* Durum */}
                        <TableCell className="py-2.5 text-center">
                          <div className="flex flex-col items-center gap-0.5">
                            <Badge
                              className={`text-[10px] px-2 py-0.5 cursor-default ${fDisplay.badgeClass}`}
                              title={`Son guncelleme: ${timeAgoTr(device.last_status_update)}`}
                              data-testid={`device-status-badge-${device.id}`}
                            >
                              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${fDisplay.dotClass}`} />
                              {fDisplay.label}
                            </Badge>
                            {device.pending_action ? (
                              <Badge className="text-[9px] px-1.5 py-0 bg-amber-100 text-amber-700 animate-pulse" data-testid={`pending-badge-${device.id}`}>
                                {device.pending_action === "reopen" ? "Acma Bkl." :
                                 device.pending_action === "cutoff" ? "Kesme Bkl." :
                                 device.pending_action === "turn_on" ? "Acma Bkl." :
                                 device.pending_action === "turn_off" ? "Kapatma Bkl." : "Beklemede"}
                              </Badge>
                            ) : device.last_command_status === "failed" ? (
                              <Badge className="text-[9px] px-1.5 py-0 bg-red-100 text-red-600" data-testid={`cmd-failed-badge-${device.id}`}>
                                Son Komut Hata
                              </Badge>
                            ) : null}
                          </div>
                        </TableCell>
                        {/* Guc */}
                        <TableCell className="py-2.5 text-right text-sm hidden sm:table-cell">
                          <span className="font-mono">{device.current_power_w || 0}</span>
                          <span className="text-slate-400 text-xs ml-0.5">W</span>
                        </TableCell>
                        {/* Tuketim */}
                        <TableCell className="py-2.5 text-right text-sm hidden sm:table-cell">
                          <span className="font-mono">{device.total_energy_kwh || 0}</span>
                          <span className="text-slate-400 text-xs ml-0.5">kWh</span>
                        </TableCell>
                        {/* kWh Bakiye */}
                        <TableCell className="py-2.5 text-right">
                          {device.balance_kwh !== null && device.balance_kwh !== undefined ? (
                            <span className={`font-bold text-sm ${device.balance_kwh > 0 ? "text-emerald-700" : "text-red-600"}`}>
                              {device.balance_kwh.toFixed(2)} kWh
                            </span>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </TableCell>
                        {/* Islem */}
                        {user?.role !== "tenant" && (
                          <TableCell className="py-2.5">
                            <div className="flex items-center justify-center gap-1">
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500"
                                onClick={() => handleRefreshStatus(device.id)}
                                disabled={controlLoading[`status_${device.id}`]}
                                data-testid={`refresh-status-btn-${device.id}`}
                                title={t("devices.refresh_status")}>
                                {controlLoading[`status_${device.id}`] ? <Spinner size={12} className="animate-spin" /> : <ArrowsClockwise size={14} />}
                              </Button>
                              {hasPerm("device_control") && (
                              <>
                              <Button size="sm" className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleControlDevice(device.id, "on")}
                                disabled={controlLoading[device.id]}
                                data-testid={`turn-on-btn-${device.id}`}>
                                <Power size={12} className="mr-0.5" /> {t("devices.turn_on")}
                              </Button>
                              <Button size="sm" variant="destructive" className="h-7 px-2 text-xs"
                                onClick={() => handleControlDevice(device.id, "off")}
                                disabled={controlLoading[device.id]}
                                data-testid={`turn-off-btn-${device.id}`}>
                                <Power size={12} className="mr-0.5" /> {t("devices.turn_off")}
                              </Button>
                              </>
                              )}
                              {hasPerm("device_reassign") && (
                              <>
                              {!device.unit_id ? (
                                <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-amber-300 text-amber-700"
                                  onClick={() => openAssignUnitDialog(device)}
                                  data-testid={`assign-unit-btn-${device.id}`}
                                  title={t("devices.assign_unit")}>
                                  <House size={12} />
                                </Button>
                              ) : (
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-indigo-600"
                                  onClick={() => openAssignUnitDialog(device)}
                                  data-testid={`change-unit-btn-${device.id}`}
                                  title={t("devices.change_unit")}>
                                  <ArrowsClockwise size={12} />
                                </Button>
                              )}
                              </>
                              )}
                              {hasPerm("device_edit") && (
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-amber-600"
                                onClick={() => openEditDeviceDialog(device)}
                                data-testid={`edit-device-btn-${device.id}`}
                                title={t("common.edit")}>
                                <Pencil size={12} />
                              </Button>
                              )}
                              {isAdmin && (
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => { setDeviceToDelete(device); setDeleteConfirmOpen(true); }}
                                data-testid={`delete-device-btn-${device.id}`}
                                title="Sayaci Sil">
                                <Trash size={12} />
                              </Button>
                              )}
                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-indigo-600 border-indigo-200"
                                onClick={() => navigate(`/devices/${device.id}`)}
                                data-testid={`detail-btn-${device.id}`}>
                                <ArrowRight size={12} className="mr-0.5" /> {t("devices.detail")}
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {filteredDevices.length > 0 && (
        <Pagination total={filteredDevices.length} page={page} pageSize={pageSize}
          onPageChange={setPage} onPageSizeChange={setPageSize} />
      )}

      {/* Assign to Unit Dialog */}
      <Dialog open={assignUnitDialog} onOpenChange={setAssignUnitDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("devices.assign_title")} - {selectedDevice?.name}</DialogTitle>
            <DialogDescription>{t("devices.assign_unit")}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {availableUnits.length === 0 ? (
              <div className="text-center py-8">
                <House size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">Atanabilir bos daire yok</p>
                <p className="text-xs text-slate-400 mt-1">Once bir apartmana daire ekleyin</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {availableUnits.map((unit) => (
                  <button key={unit.id} onClick={() => handleAssignToUnit(unit.id)}
                    disabled={formLoading}
                    className="w-full flex items-center justify-between p-3 border border-slate-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                    data-testid={`select-unit-${unit.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <House size={16} className="text-emerald-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-slate-900 text-sm">{unit.apartment_name} - {unit.name}</p>
                        {unit.customer_name && <p className="text-xs text-indigo-500">{t("common.customer")}: {unit.customer_name}</p>}
                        {unit.tenant_name && <p className="text-xs text-slate-500">{t("common.tenant")}: {unit.tenant_name}</p>}
                      </div>
                    </div>
                    <ArrowRight size={16} className="text-slate-400" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Device Dialog */}
      <Dialog open={editDeviceDialog} onOpenChange={setEditDeviceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("devices.edit_title")}</DialogTitle>
            <DialogDescription>{t("devices.edit_desc")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateDevice}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-device-name">{t("devices.name")}</Label>
                <Input id="edit-device-name" value={editDeviceName}
                  onChange={(e) => setEditDeviceName(e.target.value)}
                  required data-testid="edit-device-name-input" />
              </div>
              {isAdmin && <p className="text-xs text-slate-500">Seri No: {editDeviceTarget?.tuya_device_id}</p>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDeviceDialog(false)}>{t("common.cancel")}</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={formLoading} data-testid="edit-device-submit-btn">
                {formLoading ? t("common.updating") : t("common.update")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600">Sayac Sil</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-2">
            <p className="text-sm text-slate-700">
              <span className="font-semibold">{deviceToDelete?.name}</span> sayacini silmek istediginize emin misiniz?
            </p>
            <p className="text-xs text-red-500">Bu islem geri alinamaz. Iliskili alarmlar ve bekleyen aksiyonlar da silinecektir.</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setDeleteConfirmOpen(false); setDeviceToDelete(null); }}
              data-testid="delete-device-cancel-btn">{t("common.cancel")}</Button>
            <Button variant="destructive" data-testid="delete-device-confirm-btn"
              onClick={async () => {
                if (!deviceToDelete) return;
                await handleDeleteDevice(deviceToDelete.id);
                setDeleteConfirmOpen(false);
                setDeviceToDelete(null);
              }}>
              <Trash size={14} className="mr-1" /> Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DevicesPage;
